import React, { FormEvent, useState, useEffect, useContext } from "react";
import { useHistory } from "react-router-dom";
import AuthContext from "../../../contexts/AuthContext";
import { RouteProps } from "react-router-dom";

import Input from "../../../components/Form/Input";
import Button from "../../../components/Form/Button";
import Select from "../../../components/Form/Select";

import Message from "./Message";
import { SetModal } from "../../../components/Modal";

import { SimpleResponse } from "../../../services/api/Request";
import Forecast, { IForecast, IChannel, IStatus } from "../../../services/api/Forecast";
import WeekPeriod from "../../../services/api/WeekPeriod";

interface FormProps extends RouteProps {
  forecast: IForecast;
  month: number;
  year: number;
}

const Form: React.FC<FormProps> = ({ forecast, month, year }) => {
  const { user } = useContext(AuthContext);
  const history = useHistory();
  const readOnlyStatus = ["Viewer", "Sender"].indexOf(user!.profile.name) > -1;
  const readOnlyForm = ["Viewer", "Approver"].indexOf(user!.profile.name) > -1;
  const [response, setResponse] = useState<SimpleResponse>();
  const [status, setStatus] = useState<IStatus[]>([]);
  const [loading, isLoading] = useState<Boolean>(true);
  const [weekPeriod, setWeekPeriod] = useState<boolean[]>([false, false, false, false]);

  const isUpdate = forecast && forecast.id > 0;

  let weeks = [
    { label: "Week 1", key: "Week1" },
    { label: "Week 2", key: "Week2" },
    { label: "Week 3", key: "Week3" },
    { label: "Week 4", key: "Week4" },
  ];

  useEffect(() => {
    Promise.all([Forecast.GetAllStatus(), WeekPeriod.GetForecast()])
      .then((value) => {
        const day = new Date().getDate();
        setStatus(value[0]);
        setWeekPeriod([
          day >= value[1].startDateW1 && day <= value[1].endDateW1,
          day >= value[1].startDateW2 && day <= value[1].endDateW2,
          day >= value[1].startDateW3 && day <= value[1].endDateW3,
          day >= value[1].startDateW4 && day <= value[1].endDateW4,
        ]);

        isLoading(false);
        getTotalValue();
      })
      .catch(console.log);
  }, []);

  async function handleFormSubmit(event: FormEvent) {
    event.preventDefault();
    const formData: FormData = new FormData(event.target as HTMLFormElement);
    let data: any = {
      groupId: forecast.groupId,
      channels: []
    };

    formData.forEach((value: FormDataEntryValue, key: string) => {
      if (key.indexOf("channel") === -1) data[key] = value;
    });

    forecast.channels.forEach((value: IChannel, key: number) => {
      const elName: string = "channel" + value.channelId;
      const fw1: string | null = normalizeNumber(formData.get(elName + "[tradeSalesForecastWeek1]"));
      const fw2: string | null = normalizeNumber(formData.get(elName + "[tradeSalesForecastWeek2]"));
      const fw3: string | null = normalizeNumber(formData.get(elName + "[tradeSalesForecastWeek3]"));
      const fw4: string | null = normalizeNumber(formData.get(elName + "[tradeSalesForecastWeek4]"));

      const MTDW1: string | null = normalizeNumber(formData.get(elName + "[tradeSalesMonthTodayWeek1]"));
      const MTDW2: string | null = normalizeNumber(formData.get(elName + "[tradeSalesMonthTodayWeek2]"));
      const MTDW3: string | null = normalizeNumber(formData.get(elName + "[tradeSalesMonthTodayWeek3]"));
      const MTDW4: string | null = normalizeNumber(formData.get(elName + "[tradeSalesMonthTodayWeek4]"));

      //console.log(elName, fw1);

      data["channels"].push({
        channelId: value.channelId,
        id: value.id,
        tradeSalesForecastWeek1: fw1 ? fw1 : 0,
        tradeSalesForecastWeek2: fw2 ? fw2 : 0,
        tradeSalesForecastWeek3: fw3 ? fw3 : 0,
        tradeSalesForecastWeek4: fw4 ? fw4 : 0,
        tradeSalesMonthTodayWeek1: MTDW1 ? MTDW1 : 0,
        tradeSalesMonthTodayWeek2: MTDW2 ? MTDW2 : 0,
        tradeSalesMonthTodayWeek3: MTDW3 ? MTDW3 : 0,
        tradeSalesMonthTodayWeek4: MTDW4 ? MTDW4 : 0
      });
    });

    try {
      const response: SimpleResponse = await (isUpdate
        ? Forecast.Update(forecast.id, data)
        : Forecast.Create(year, month, data));
      setResponse(response);
    } catch (error) {
      console.log(error);
    }
  }

  function normalizeNumber(value:FormDataEntryValue | null){
    return value === null ? null : (value+"").replace(/\./g,'');
  }

  async function handleFormSubmitStatus(event: FormEvent) {
    event.preventDefault();
    const formData: FormData = new FormData(event.target as HTMLFormElement);

    let data: any = {
      Id: formData.get("status")
    };

    const response: SimpleResponse = await Forecast.SetStatus(
      forecast.id,
      data
    );
    setResponse(response);
  }

  function listWeek(obj: any, index:number): any {
    return (
      <div className="form form--border" key={obj.key}>
        <h3 className="form__title">{"Forecast " + obj.label}</h3>
        <div className="form__fields--sideBySide">
          {forecast.channels.map((value: any) => {
            return (
              <Input
                masked={true}
                disabled={readOnlyForm}
                readOnly={readOnlyStatus && !weekPeriod[index]}
                key={
                  "channel" +
                  value.channelId +
                  "[tradeSalesForecast" +
                  obj.key +
                  "]"
                }
                name={
                  "channel" +
                  value.channelId +
                  "[tradeSalesForecast" +
                  obj.key +
                  "]"
                }
                title={value.channelName}
                value={value["tradeSalesForecast" + obj.key]}
                type="number"
              />
            );
          })}

          <div className="form-group">
            <label className="form-label form__label form__total">Total Trade Sales: <span>0</span></label>
          </div>
        </div>
      </div>
    );
  }
  function listWeekMT(obj: any, index:number): any {
    return (
      <div className="form form--border" key={obj.key}>
        <h3 className="form__title">{"Month to Date " + obj.label}</h3>
        <div className="form__fields--sideBySide">
          {forecast.channels.map((value: any) => {
            return (
              <Input
                masked={true}
                disabled={readOnlyForm}
                readOnly={readOnlyStatus && !weekPeriod[index]}
                key={
                  "channel" +
                  value.channelId +
                  "[tradeSalesMonthToday" +
                  obj.key +
                  "]"
                }
                name={
                  "channel" +
                  value.channelId +
                  "[tradeSalesMonthToday" +
                  obj.key +
                  "]"
                }
                title={value.channelName}
                value={value["tradeSalesMonthToday" + obj.key]}
                type="number"
              />
            );
          })}

          <div className="form-group">
            <label className="form-label form__label form__total">Total Trade Sales: <span>0</span></label>
          </div>
        </div>
      </div>
    );
  }

  //#region Calculete total
  function setTotalValue(box: Element) {
    let total = 0;
    box.querySelectorAll(".form-group input").forEach((input: Element) => {
      if (input.classList.contains("financialsales"))
        return;
      const _value = parseFloat(("0" + input.getAttribute("value")).replace(/\D+/gi, ''));
      total += _value;
    });

    let formTotal = box.querySelector(".form__total span");
    if (formTotal)
      formTotal.innerHTML = (total + "").toString().replace(/\B(?=(\d{3})+(?!\d))/gi, "."); // format thousands
  }

  function getTotalValue() {
    document.querySelectorAll(".form__group-container .form").forEach(box => {
      setTotalValue(box); // calculate total when the page reloads

      box.querySelectorAll(".form-group input").forEach((input: Element) => {
        input.addEventListener("keyup", (event) => {
          setTotalValue(box); // calculate total when the field changed
        })
      });
    });
  }
  //#endregion

  return (
    <React.Fragment>
          {response ? SetModal.FromResponse(response, () => {
              setResponse(undefined);
              if (response.status === 200) {
                  history.push({ pathname: "/empty" });
                  history.goBack();
              }
          }) : null}
      {loading ? (
        <span>Loading...</span>
      ) : (
        <React.Fragment>
          <form
            className="container details__form"
            onSubmit={handleFormSubmitStatus}
          >
            <div className="details__select">
              <Select
                disabled={readOnlyStatus || forecast.id === 0}
                labelClass="form__label"
                name="status"
                title="Status"
                options={status.map((obj) => {
                  return { value: obj.id, label: obj.name };
                })}
                defaultValue={forecast.status.id}
              />
              {readOnlyStatus || forecast.id === 0 ? null : (
                <Button
                  className="button button--big button--blue details__button--form"
                  title="Apply"
                />
              )}
            </div>
          </form>

          <form
            className="container details__forms form__group-container"
            onSubmit={handleFormSubmit}
          >
            {weeks.map(listWeek)}
            {weeks.map(listWeekMT)}
            {readOnlyForm ? null : (
              <Button
                className="button button--big button--blue details__button--form"
                title={isUpdate ? "Save" : "Send"}
              />
            )}
          </form>
        </React.Fragment>
              )}
          <Message forecastId={forecast.id} groupId={forecast.groupId } monthId={month} currentYear={year} />
    </React.Fragment>
  );
};

export default Form;
